import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { 
  Settings,
  Mail,
  Bell,
  Server,
  Lock,
  Send,
  CheckCircle2,
  XCircle,
  Loader2,
  Eye,
  EyeOff,
  Shield,
  Globe,
  Building2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ConfigurationPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // General Configuration
  const [generalConfig, setGeneralConfig] = useState({
    frontend_url: '',
    app_name: 'CCI Togo - Suivi Consultant'
  });

  // SMTP Configuration
  const [smtpConfig, setSmtpConfig] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'CCI Togo',
    smtp_encryption: 'tls',
    smtp_enabled: false,
    smtp_password_set: false
  });
  
  // Notification Configuration
  const [notifConfig, setNotifConfig] = useState({
    notify_sme_on_intervention: true,
    notify_admin_on_report: true,
    notify_consultant_on_validation: true,
    notify_admin_on_new_user: false,
    notify_on_mission_completed: true
  });
  
  // Test email
  const [testEmail, setTestEmail] = useState('');
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    fetchConfigurations();
  }, []);

  const fetchConfigurations = async () => {
    try {
      setLoading(true);
      const [generalRes, smtpRes, notifRes] = await Promise.all([
        axios.get(`${API}/admin/config/general`),
        axios.get(`${API}/admin/config/smtp`),
        axios.get(`${API}/admin/config/notifications`)
      ]);
      
      setGeneralConfig(generalRes.data);
      setSmtpConfig({
        ...smtpRes.data,
        smtp_password: '' // Don't display password
      });
      setNotifConfig(notifRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement de la configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGeneral = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/admin/config/general`, generalConfig);
      setGeneralConfig(response.data);
      toast.success('Configuration générale enregistrée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSMTP = async () => {
    setSaving(true);
    try {
      const dataToSend = { ...smtpConfig };
      // Only send password if it was changed
      if (!dataToSend.smtp_password) {
        delete dataToSend.smtp_password;
      }
      delete dataToSend.smtp_password_set;
      
      const response = await axios.put(`${API}/admin/config/smtp`, dataToSend);
      setSmtpConfig({
        ...response.data,
        smtp_password: ''
      });
      toast.success('Configuration SMTP enregistrée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/config/notifications`, notifConfig);
      toast.success('Configuration des notifications enregistrée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Veuillez entrer une adresse email');
      return;
    }
    
    setTesting(true);
    setTestResult(null);
    try {
      const response = await axios.post(`${API}/admin/config/smtp/test`, {
        to_email: testEmail
      });
      setTestResult(response.data);
      if (response.data.success) {
        toast.success('Email de test envoyé avec succès');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'envoi';
      setTestResult({ success: false, message });
      toast.error(message);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 fade-in" data-testid="configuration-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans'] flex items-center gap-3">
            <Settings className="w-8 h-8" />
            Configuration
          </h1>
          <p className="text-slate-500 mt-1">
            Paramétrez les services de l'application
          </p>
        </div>

        <Tabs defaultValue="general" className="space-y-6">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="general" className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Général
            </TabsTrigger>
            <TabsTrigger value="smtp" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email (SMTP)
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Notifications
            </TabsTrigger>
          </TabsList>

          {/* General Configuration Tab */}
          <TabsContent value="general" className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-indigo-600" />
                  Configuration générale
                </CardTitle>
                <CardDescription>
                  Paramètres généraux de l'application
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Frontend URL */}
                <div className="space-y-2">
                  <Label htmlFor="frontend_url">URL du site (Frontend)</Label>
                  <Input
                    id="frontend_url"
                    placeholder="https://digiconsult.ccit.tg"
                    value={generalConfig.frontend_url}
                    onChange={(e) => setGeneralConfig({...generalConfig, frontend_url: e.target.value})}
                    data-testid="frontend-url-input"
                  />
                  <p className="text-sm text-slate-500">
                    Cette URL est utilisée pour générer les liens dans les emails (ex: réinitialisation de mot de passe)
                  </p>
                </div>

                {/* App Name */}
                <div className="space-y-2">
                  <Label htmlFor="app_name">Nom de l'application</Label>
                  <Input
                    id="app_name"
                    placeholder="CCI Togo - Suivi Consultant"
                    value={generalConfig.app_name}
                    onChange={(e) => setGeneralConfig({...generalConfig, app_name: e.target.value})}
                    data-testid="app-name-input"
                  />
                </div>

                {/* Info box */}
                <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                  <p className="text-sm text-indigo-800">
                    <Globe className="w-4 h-4 inline mr-2" />
                    <strong>Important :</strong> Assurez-vous que l'URL du site correspond à l'adresse réelle 
                    de votre application pour que les liens dans les emails fonctionnent correctement.
                  </p>
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t">
                  <Button 
                    onClick={handleSaveGeneral} 
                    disabled={saving}
                    className="bg-slate-900 hover:bg-slate-800"
                    data-testid="save-general-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Enregistrement...
                      </>
                    ) : (
                      'Enregistrer'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SMTP Configuration Tab */}
          <TabsContent value="smtp" className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="w-5 h-5 text-blue-600" />
                  Configuration du serveur SMTP
                </CardTitle>
                <CardDescription>
                  Configurez les paramètres de votre serveur email pour envoyer des notifications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable/Disable - More prominent */}
                <div className={`flex items-center justify-between p-4 rounded-lg border-2 transition-colors ${
                  smtpConfig.smtp_enabled 
                    ? 'bg-emerald-50 border-emerald-300' 
                    : 'bg-amber-50 border-amber-300'
                }`}>
                  <div className="space-y-0.5">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      {smtpConfig.smtp_enabled ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-amber-600" />
                      )}
                      {smtpConfig.smtp_enabled ? 'Service email activé' : 'Service email désactivé'}
                    </Label>
                    <p className="text-sm text-slate-600">
                      {smtpConfig.smtp_enabled 
                        ? 'Les notifications seront envoyées par email'
                        : 'Cliquez sur le bouton pour activer l\'envoi d\'emails'}
                    </p>
                  </div>
                  <Switch
                    checked={smtpConfig.smtp_enabled}
                    onCheckedChange={(checked) => setSmtpConfig({...smtpConfig, smtp_enabled: checked})}
                    data-testid="smtp-enabled-switch"
                    className="data-[state=checked]:bg-emerald-600"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Server Host */}
                  <div className="space-y-2">
                    <Label htmlFor="smtp_host">Serveur SMTP</Label>
                    <Input
                      id="smtp_host"
                      placeholder="smtp.example.com"
                      value={smtpConfig.smtp_host}
                      onChange={(e) => setSmtpConfig({...smtpConfig, smtp_host: e.target.value})}
                      data-testid="smtp-host-input"
                    />
                  </div>

                  {/* Port */}
                  <div className="space-y-2">
                    <Label htmlFor="smtp_port">Port</Label>
                    <Input
                      id="smtp_port"
                      type="number"
                      placeholder="587"
                      value={smtpConfig.smtp_port}
                      onChange={(e) => setSmtpConfig({...smtpConfig, smtp_port: parseInt(e.target.value) || 587})}
                      data-testid="smtp-port-input"
                    />
                  </div>

                  {/* Encryption */}
                  <div className="space-y-2">
                    <Label>Chiffrement</Label>
                    <Select 
                      value={smtpConfig.smtp_encryption} 
                      onValueChange={(v) => setSmtpConfig({...smtpConfig, smtp_encryption: v})}
                    >
                      <SelectTrigger data-testid="smtp-encryption-select">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tls">TLS (STARTTLS)</SelectItem>
                        <SelectItem value="ssl">SSL</SelectItem>
                        <SelectItem value="none">Aucun</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Username */}
                  <div className="space-y-2">
                    <Label htmlFor="smtp_user">Utilisateur</Label>
                    <Input
                      id="smtp_user"
                      placeholder="user@example.com"
                      value={smtpConfig.smtp_user}
                      onChange={(e) => setSmtpConfig({...smtpConfig, smtp_user: e.target.value})}
                      data-testid="smtp-user-input"
                    />
                  </div>

                  {/* Password */}
                  <div className="space-y-2">
                    <Label htmlFor="smtp_password">
                      Mot de passe
                      {smtpConfig.smtp_password_set && (
                        <span className="ml-2 text-xs text-emerald-600">(déjà configuré)</span>
                      )}
                    </Label>
                    <div className="relative">
                      <Input
                        id="smtp_password"
                        type={showPassword ? "text" : "password"}
                        placeholder={smtpConfig.smtp_password_set ? "••••••••" : "Entrer le mot de passe"}
                        value={smtpConfig.smtp_password}
                        onChange={(e) => setSmtpConfig({...smtpConfig, smtp_password: e.target.value})}
                        data-testid="smtp-password-input"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* From Email */}
                  <div className="space-y-2">
                    <Label htmlFor="smtp_from_email">Email expéditeur</Label>
                    <Input
                      id="smtp_from_email"
                      type="email"
                      placeholder="noreply@example.com"
                      value={smtpConfig.smtp_from_email}
                      onChange={(e) => setSmtpConfig({...smtpConfig, smtp_from_email: e.target.value})}
                      data-testid="smtp-from-email-input"
                    />
                  </div>

                  {/* From Name */}
                  <div className="space-y-2">
                    <Label htmlFor="smtp_from_name">Nom expéditeur</Label>
                    <Input
                      id="smtp_from_name"
                      placeholder="CCI Togo"
                      value={smtpConfig.smtp_from_name}
                      onChange={(e) => setSmtpConfig({...smtpConfig, smtp_from_name: e.target.value})}
                      data-testid="smtp-from-name-input"
                    />
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t">
                  <Button 
                    onClick={handleSaveSMTP} 
                    disabled={saving}
                    className="bg-slate-900 hover:bg-slate-800"
                    data-testid="save-smtp-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Enregistrement...
                      </>
                    ) : (
                      'Enregistrer la configuration'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Test Email Card */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="w-5 h-5 text-emerald-600" />
                  Tester la configuration
                </CardTitle>
                <CardDescription>
                  Envoyez un email de test pour vérifier que la configuration fonctionne
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <Input
                      type="email"
                      placeholder="votre@email.com"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      data-testid="test-email-input"
                    />
                  </div>
                  <Button 
                    onClick={handleTestEmail}
                    disabled={testing || !smtpConfig.smtp_enabled}
                    variant="outline"
                    data-testid="send-test-btn"
                  >
                    {testing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Envoi...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Envoyer un test
                      </>
                    )}
                  </Button>
                </div>

                {!smtpConfig.smtp_enabled && (
                  <p className="text-sm text-amber-600 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Activez le service email pour pouvoir envoyer des tests
                  </p>
                )}

                {testResult && (
                  <div className={`p-4 rounded-lg flex items-center gap-3 ${
                    testResult.success 
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                    {testResult.success ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <XCircle className="w-5 h-5" />
                    )}
                    <span>{testResult.message}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Configuration Tab */}
          <TabsContent value="notifications" className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-amber-600" />
                  Paramètres des notifications
                </CardTitle>
                <CardDescription>
                  Choisissez les événements qui déclenchent l'envoi d'emails
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {!smtpConfig.smtp_enabled && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-amber-700 flex items-center gap-2">
                      <Shield className="w-5 h-5" />
                      Le service email est désactivé. Configurez et activez le SMTP pour envoyer des notifications.
                    </p>
                  </div>
                )}

                {/* Notification Options */}
                <div className="space-y-4">
                  {/* Notify SME on intervention */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="space-y-0.5">
                      <Label className="text-base">Nouvelle intervention</Label>
                      <p className="text-sm text-slate-500">
                        Notifier la PME lorsqu'une nouvelle intervention est créée
                      </p>
                    </div>
                    <Switch
                      checked={notifConfig.notify_sme_on_intervention}
                      onCheckedChange={(checked) => setNotifConfig({...notifConfig, notify_sme_on_intervention: checked})}
                      disabled={!smtpConfig.smtp_enabled}
                      data-testid="notif-sme-intervention-switch"
                    />
                  </div>

                  {/* Notify admin on report */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="space-y-0.5">
                      <Label className="text-base">Nouveau signalement</Label>
                      <p className="text-sm text-slate-500">
                        Notifier les administrateurs lorsqu'un consultant signale un problème
                      </p>
                    </div>
                    <Switch
                      checked={notifConfig.notify_admin_on_report}
                      onCheckedChange={(checked) => setNotifConfig({...notifConfig, notify_admin_on_report: checked})}
                      disabled={!smtpConfig.smtp_enabled}
                      data-testid="notif-admin-report-switch"
                    />
                  </div>

                  {/* Notify consultant on validation */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="space-y-0.5">
                      <Label className="text-base">Validation d'intervention</Label>
                      <p className="text-sm text-slate-500">
                        Notifier le consultant lorsqu'une PME valide ou rejette son intervention
                      </p>
                    </div>
                    <Switch
                      checked={notifConfig.notify_consultant_on_validation}
                      onCheckedChange={(checked) => setNotifConfig({...notifConfig, notify_consultant_on_validation: checked})}
                      disabled={!smtpConfig.smtp_enabled}
                      data-testid="notif-consultant-validation-switch"
                    />
                  </div>

                  {/* Notify admin on new user */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="space-y-0.5">
                      <Label className="text-base">Nouvel utilisateur</Label>
                      <p className="text-sm text-slate-500">
                        Notifier les administrateurs lors de l&apos;inscription d&apos;un nouvel utilisateur
                      </p>
                    </div>
                    <Switch
                      checked={notifConfig.notify_admin_on_new_user}
                      onCheckedChange={(checked) => setNotifConfig({...notifConfig, notify_admin_on_new_user: checked})}
                      disabled={!smtpConfig.smtp_enabled}
                      data-testid="notif-admin-new-user-switch"
                    />
                  </div>

                  {/* Notify on mission completed */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="space-y-0.5">
                      <Label className="text-base">Mission terminée</Label>
                      <p className="text-sm text-slate-500">
                        Notifier le consultant lorsque sa mission est complétée (rapport final soumis)
                      </p>
                    </div>
                    <Switch
                      checked={notifConfig.notify_on_mission_completed}
                      onCheckedChange={(checked) => setNotifConfig({...notifConfig, notify_on_mission_completed: checked})}
                      disabled={!smtpConfig.smtp_enabled}
                      data-testid="notif-mission-completed-switch"
                    />
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t">
                  <Button 
                    onClick={handleSaveNotifications} 
                    disabled={saving || !smtpConfig.smtp_enabled}
                    className="bg-slate-900 hover:bg-slate-800"
                    data-testid="save-notifications-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Enregistrement...
                      </>
                    ) : (
                      'Enregistrer les notifications'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};
